import once from 'lodash/once';
import { GetServerSidePropsContext, NextPage } from 'next';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import CareerSeeker from 'wwg-utilities/dist/collections/career-seeker';
import Program from 'wwg-utilities/dist/collections/program';
import Provider from 'wwg-utilities/dist/collections/provider';
import About from '../../../../components/training/About';
import Admissions from '../../../../components/training/Admissions';
import Applying from '../../../../components/training/Applying';
import Banner from '../../../../components/training/Banner';
import Contact from '../../../../components/training/Contact';
import InfoTabs from '../../../../components/training/InfoTabs';
import Map from '../../../../components/training/Map';
import QuickFacts from '../../../../components/training/QuickFacts';
import RelatedPrograms from '../../../../components/training/RelatedPrograms';
import SmallImage from '../../../../components/training/SmallImage';
import GNOFooter from '../../../../components/wherewego/Footer';
import GNOHeader from '../../../../components/wherewego/Header';
import addOnePageViewRequest from '../../../../lib/api/addOnePageViewRequest';
import frontendURL from '../../../../lib/config/project/frontendURL';
import { siteTitle } from '../../../../lib/config/project/meta';
import programsPageGSSP from '../../../../lib/gssp/programPageGetSSP';
import useGAPageLoad from '../../../../lib/hooks/GA/gaPageLoad';
import initUser from '../../../../lib/hooks/initUser';
import { useRegion } from '../../../../lib/providers/regionProvider';

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  return programsPageGSSP(ctx, { draft: false });
};

type TrainingInstitutionProgramPageProps = {
  serverProgram: Program;
  serverUser: any | null;
  serverSeeker: CareerSeeker | null;
  draft?: boolean;
  bigImage: string;
  smallImage: string;
};

const TrainingInstitutionProgramPage: NextPage<
  TrainingInstitutionProgramPageProps
> = ({
  serverProgram,
  serverUser,
  serverSeeker,
  draft,
  bigImage,
  smallImage,
}) => {
  const [program, _setProgram] = useState(serverProgram);
  const addOneOnce = once(addOnePageViewRequest);
  useGAPageLoad(serverSeeker);
  initUser(serverUser, serverSeeker);

  const { region } = useRegion();
  useEffect(() => {
    addOneOnce(program.id);
  }, []);

  return (
    <>
      <Head>
        <title>{`${program.name} at ${
          (program.institution as Provider).name
        } | ${siteTitle}`}</title>
        <meta
          name="description"
          content={program.about.substring(0, 160)}
        />
        <meta
          property="og:title"
          content={`${program.name} at ${(program.institution as Provider).name} | ${siteTitle}`}
        />
        <meta property="og:description" content={program.about.substring(0, 160)} />
        <meta property="og:type" content="article" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'EducationalOccupationalProgram',
              name: program.name,
              description: program.about,
              provider: {
                '@type': 'EducationalOrganization',
                name: (program.institution as Provider).name,
                ...(program.address && {
                  address: {
                    '@type': 'PostalAddress',
                    addressLocality: program.address.city,
                    addressRegion: program.address.state,
                    postalCode: program.address.zip,
                    addressCountry: 'US',
                  },
                }),
              },
              ...(program.durationData && {
                timeToComplete: `P${program.durationData.timeMax || program.durationData.timeMin || ''}M`,
              }),
              ...(program.costData?.costMin != null && {
                offers: {
                  '@type': 'Offer',
                  priceSpecification: {
                    '@type': 'PriceSpecification',
                    price: program.costData.costMin,
                    priceCurrency: 'USD',
                  },
                },
              }),
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: frontendURL },
                { '@type': 'ListItem', position: 2, name: 'Training Programs', item: `${frontendURL}/training` },
                { '@type': 'ListItem', position: 3, name: `${program.name} at ${(program.institution as Provider).name}` },
              ],
            }),
          }}
        />
      </Head>
      <GNOHeader />

      <main className="program-grid">
        <Banner
          bigImage={bigImage}
          draft={draft}
          program={program}
          region={region?.slug}
        />
        <section className="hidden col-start-3 col-end-4 row-span-4 row-start-1 mx-auto lg:block ">
          <div className="sticky top-[40px]">
            <QuickFacts program={program} region={region?.slug} />
          </div>
        </section>
        <section className="container relative my-8 lg:transform md:mx-auto lg:my-0 lg:col-start-2 lg:col-end-3 lg:row-start-2">
          <section className="block -mt-10 lg:hidden">
            <QuickFacts program={program} region={region?.slug} />
          </section>
          <About program={program} />
          <InfoTabs program={program} />
          <SmallImage smallImage={smallImage} program={program} />
          <Admissions program={program} />
          <Applying program={program} />
          <Contact program={program} />
          <Map program={program} />
          <RelatedPrograms program={program} />
        </section>
      </main>
      <GNOFooter />
    </>
  );
};

export default TrainingInstitutionProgramPage;
